// ============================================================================
// RAID BADGE
//
// The "3 open" chip on an artefact card. Clicking it expands the inline list,
// so the badge and the inline list are one control rather than two — the count
// keeps cards compact when a story has several items hanging off it.
//
// Blocking items get a distinct treatment: a story blocked by an open
// dependency is a different situation from one that merely has questions
// attached, and the register is read at a glance during a workshop.
// ============================================================================

import React from 'react';
import { ShieldAlert, ChevronDown, ChevronUp, Plus } from 'lucide-react';

export interface RaidBadgeProps {
  count: number;
  openCount: number;
  blockingCount: number;
  isExpanded: boolean;
  onToggle: () => void;
  /** Shown when there is nothing yet, so an item can be raised from the card. */
  onAdd?: () => void;
}

export function RaidBadge({ count, openCount, blockingCount, isExpanded, onToggle, onAdd }: Readonly<RaidBadgeProps>) {
  if (count === 0) {
    if (!onAdd) return null;
    return (
      <button
        type="button"
        onClick={onAdd}
        className="mt-1.5 flex items-center gap-1 text-[11px] text-slate-300 hover:text-primary transition-colors opacity-0 group-hover:opacity-100"
        title="Raise a risk, assumption, issue, dependency or question against this"
      >
        <Plus size={11} /> RAID
      </button>
    );
  }

  const blocked = blockingCount > 0;
  const tone = blocked
    ? 'bg-red-50 text-red-600 border-red-200 hover:bg-red-100'
    : 'bg-slate-100 text-slate-500 border-slate-200 hover:bg-slate-200';

  let label: string;
  if (blocked) label = `${blockingCount} blocking`;
  else if (openCount > 0) label = `${openCount} open`;
  else label = `${count} closed`;

  return (
    <button
      type="button"
      onClick={onToggle}
      className={`mt-1.5 flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-[10px] font-semibold transition-colors ${tone}`}
      title={`${count} RAID item${count === 1 ? '' : 's'} linked`}
    >
      <ShieldAlert size={10} />
      {label}
      {isExpanded ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
    </button>
  );
}
